import { Button, Card, CardBody, CardHeader, Input } from "@heroui/react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import logo from "@/assets/images/bomiel.png";
import { changePassword } from "@/lib/auth";
import { useAuth } from "@/lib/auth-context";
import { errorMessage } from "@/lib/api";
import { PASSWORD_REQUIREMENT_TEXT, passwordPolicyError } from "@/lib/password";
import { showSuccess } from "@/lib/toast";

/**
 * パスワード変更画面。
 *
 * 仮パスワードのまま（`must_change_password`）のときは [[AuthGate]] がここへ
 * 送り、変更するまで他の画面へ行けない。自分から変えに来た場合は戻れる。
 *
 * メールを使わない運用のため、パスワードを忘れた場合の再設定はここではなく
 * 管理者による仮パスワードの再発行で行う。
 */
export default function PasswordChangePage() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const forced = user?.must_change_password === true;

  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!current || !next) {
      setError("現在のパスワードと新しいパスワードを入力してください。");
      return;
    }
    if (next !== confirm) {
      setError("新しいパスワードが一致しません。");
      return;
    }
    // 要件はサーバでも検証されるが、送信して 400 を見る前に理由を出す。
    const policyError = passwordPolicyError(next);
    if (policyError) {
      setError(policyError);
      return;
    }

    setSaving(true);
    try {
      await changePassword(current, next);
      // 認証状態を取り直して must_change_password を落とす。
      await refresh();
      showSuccess("パスワードを変更しました。");
      navigate("/", { replace: true });
    } catch (e) {
      setError(errorMessage(e, "パスワードを変更できませんでした。"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex min-h-dvh items-center justify-center bg-page p-6">
      <Card className="w-full max-w-md" shadow="md">
        <CardHeader className="flex-col items-start gap-2 px-6 pt-6">
          <img src={logo} alt="BOMIEL" className="h-8 w-auto" />
          <h1 className="text-xl font-bold text-brand-500">パスワードの変更</h1>
          <p className="text-sm text-default-500">
            {forced
              ? "仮パスワードでログインしています。続けるには、新しいパスワードを設定してください。"
              : "現在のパスワードを確認したうえで、新しいパスワードを設定します。"}
          </p>
        </CardHeader>

        <CardBody className="flex flex-col gap-4 px-6 pb-6">
          <Input
            label={forced ? "仮パスワード" : "現在のパスワード"}
            labelPlacement="outside"
            placeholder=" "
            aria-label="現在のパスワード"
            type="password"
            variant="bordered"
            radius="sm"
            autoComplete="current-password"
            value={current}
            onValueChange={(v) => {
              setCurrent(v);
              setError(null);
            }}
          />
          <Input
            label="新しいパスワード"
            labelPlacement="outside"
            placeholder=" "
            aria-label="新しいパスワード"
            type="password"
            variant="bordered"
            radius="sm"
            autoComplete="new-password"
            description={PASSWORD_REQUIREMENT_TEXT}
            value={next}
            onValueChange={(v) => {
              setNext(v);
              setError(null);
            }}
          />
          <Input
            label="新しいパスワード（確認）"
            labelPlacement="outside"
            placeholder=" "
            aria-label="新しいパスワード（確認）"
            type="password"
            variant="bordered"
            radius="sm"
            autoComplete="new-password"
            value={confirm}
            onValueChange={(v) => {
              setConfirm(v);
              setError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") void submit();
            }}
          />

          {error && <p className="text-sm text-danger">{error}</p>}

          <p className="text-tiny text-default-500">
            変更すると、この端末以外のログインは解除されます。
          </p>

          <div className="flex justify-end gap-2">
            {!forced && (
              <Button
                variant="light"
                radius="full"
                data-testid="cancel"
                onPress={() => navigate(-1)}
              >
                キャンセル
              </Button>
            )}
            <Button
              color="primary"
              radius="full"
              data-testid="submit"
              isLoading={saving}
              onPress={() => void submit()}
            >
              変更する
            </Button>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
